import React from 'react';
import { AlertCircle, ExternalLink, WifiOff } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { useUserPermissions } from '../hooks/useUserPermissions.ts';
import type { ParsedApiError } from '../services/api.ts';

type AlertVariant = 'card' | 'top-bar' | 'bottom-bar';

interface WhatsAppIdentityErrorAlertProps {
  error: ParsedApiError;
  variant?: AlertVariant;
  onOpenExisting?: () => void;
  existingActionLabel?: string;
  showExistingAction?: boolean;
}

const variantClasses: Record<AlertVariant, string> = {
  card: 'rounded-xl border p-3',
  'top-bar': 'border-t px-4 py-3',
  'bottom-bar': 'border-b px-4 py-3',
};

const WhatsAppIdentityErrorAlert: React.FC<WhatsAppIdentityErrorAlertProps> = ({
  error,
  variant = 'card',
  onOpenExisting,
  existingActionLabel = 'Abrir registro existente',
  showExistingAction,
}) => {
  const { isDark } = useTheme();
  const { hasPermission } = useUserPermissions();
  const isConnectionUnavailable = error.code === 'whatsapp_validation_unavailable';
  const canManageWhatsAppConnection = hasPermission('whatsapp.conexao');
  const hasExistingIdentity =
    showExistingAction ??
    Boolean(
      error.existingContactId ||
        error.existingLeadId ||
        error.existingPatientId,
    );
  const isBar = variant !== 'card';

  return (
    <div
      role="alert"
      className={[
        'flex flex-col gap-3 text-sm',
        isBar ? 'sm:flex-row sm:items-center sm:justify-between' : '',
        variantClasses[variant],
        isConnectionUnavailable
          ? isDark
            ? 'border-amber-700/70 bg-amber-950/40 text-amber-100'
            : 'border-amber-200 bg-amber-50 text-amber-900'
          : isDark
            ? 'border-rose-800 bg-rose-950/40 text-rose-200'
            : 'border-rose-200 bg-rose-50 text-rose-700',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex min-w-0 items-start gap-2">
        {isConnectionUnavailable ? (
          <WifiOff className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
        ) : (
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
        )}

        <div className="min-w-0 space-y-1">
          {isConnectionUnavailable ? (
            <>
              <p className="font-semibold">Validação do WhatsApp indisponível</p>
              <p>
                Não foi possível verificar este número no WhatsApp agora. Aguarde alguns
                instantes e tente novamente. Se persistir, verifique a conexão da clínica.
              </p>
              {!canManageWhatsAppConnection && (
                <p className={isDark ? 'text-amber-200/80' : 'text-amber-800'}>
                  Se o erro persistir, peça a um administrador ou gerente para verificar a conexão.
                </p>
              )}
            </>
          ) : (
            <p>{error.message}</p>
          )}
        </div>
      </div>

      {isConnectionUnavailable && canManageWhatsAppConnection && (
        <a
          href="/canais"
          target="_blank"
          rel="noopener noreferrer"
          className={[
            'inline-flex min-h-11 flex-shrink-0 items-center justify-center gap-2 self-start rounded-xl px-3 py-2',
            'text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2',
            'focus-visible:ring-amber-500 focus-visible:ring-offset-2 sm:self-auto',
            isDark
              ? 'bg-amber-400 text-amber-950 hover:bg-amber-300 focus-visible:ring-offset-amber-950'
              : 'bg-amber-600 text-white hover:bg-amber-700 focus-visible:ring-offset-amber-50',
          ].join(' ')}
        >
          Ver conexão do WhatsApp
          <span className="sr-only"> (abre em nova aba)</span>
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
        </a>
      )}

      {!isConnectionUnavailable && hasExistingIdentity && onOpenExisting && (
        <button
          type="button"
          onClick={onOpenExisting}
          className={[
            'flex-shrink-0 self-start font-semibold text-sminy-primary hover:underline',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary',
            'focus-visible:ring-offset-2 sm:self-auto',
            isDark ? 'focus-visible:ring-offset-slate-900' : 'focus-visible:ring-offset-white',
          ].join(' ')}
        >
          {existingActionLabel}
        </button>
      )}
    </div>
  );
};

export default WhatsAppIdentityErrorAlert;
